import { useMemo, useState, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import { DatagridNames } from 'types/datagrid';
import { StockLotQuerySchema, AllocationQuerySchema } from 'types/inventory';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import {
  useGetStockLotsServerSideQuery,
  useGetAllocationsServerSideQuery,
} from 'store/slices/apiV1/inventory';
import useServerSideQuery from 'hooks/useServerSideQuery';
import {
  DISABLE_PAGE_LIMIT,
  QueryFilterOperators,
  QueryParams,
} from 'types/api';
import { KitRequestSubtype } from 'views/kitting/Kit/KitRequestContext';
import { getStockLotColumns } from 'ui-component/AllocatedPartsDatagrid/stockLotColumns';
import {
  StockLotForAllocationReasoning,
  AllocationForReasoning,
  DecoratedStockLot,
} from 'ui-component/AllocatedPartsDatagrid/types';
import { computeAllocationReasons } from 'ui-component/AllocatedPartsDatagrid/utils';
import { skipToken } from '@reduxjs/toolkit/query/react';
import useCustomTeamTypeName from 'hooks/useCustomTeamTypeName';

export const getAllocatedStockLotIds = (
  kitRequestLine: DecoratedKitRequestLine
): Set<string> => {
  const stockLotIds = new Set<string>();
  kitRequestLine.allocations?.forEach((allocation) => {
    if (allocation.stockLot?.id) {
      stockLotIds.add(allocation.stockLot.id);
    }
  });
  return stockLotIds;
};

type StockLotDetailPanelProps = {
  row: DecoratedKitRequestLine;
  kitRequest?: KitRequestSubtype | null;
};

export const StockLotDetailPanel = ({
  row,
  kitRequest,
}: StockLotDetailPanelProps) => {
  const apiRef = useGridApiRef();
  const [visibleStockLotIds, setVisibleStockLotIds] = useState<string[]>([]);
  const { handleReplaceWithCustomTeamName } = useCustomTeamTypeName();

  const v2OrgPartIds = useMemo(() => {
    const ids: string[] = [];

    if (row.v2OrgPart) {
      ids.push(row.v2OrgPart);
    }

    if (row.altV2OrgParts && row.altV2OrgParts.length > 0) {
      ids.push(...row.altV2OrgParts.filter((id): id is string => id != null));
    }

    return ids;
  }, [row.v2OrgPart, row.altV2OrgParts]);

  const facilityIds = useMemo(
    () =>
      kitRequest?.sourceLocations
        ?.map((location) => location.id)
        ?.filter((id): id is string => id != null) || [],
    [kitRequest?.sourceLocations]
  );

  const stockLotFilters = useMemo(() => {
    const baseFilters = [
      {
        field: 'v2OrgPart',
        operator: QueryFilterOperators.isAnyOf,
        value: v2OrgPartIds,
      },
      {
        field: 'quant',
        operator: QueryFilterOperators['>'],
        value: 0,
      },
    ];

    return baseFilters as QueryParams<StockLotQuerySchema>['filters'];
  }, [v2OrgPartIds]);

  const { data: stockLots, isLoading: isLoadingStockLots } = useServerSideQuery<
    StockLotQuerySchema,
    StockLotForAllocationReasoning
  >(useGetStockLotsServerSideQuery, {
    pageSize: DISABLE_PAGE_LIMIT,
    schema: [
      'part',
      'v2OrgPart',
      'id',
      'quant',
      'lotId',
      'facility',
      'team',
      'smtNoPnp',
      'quantCertain',
    ],
    pageNumber: 1,
    blended: true,
    filters: stockLotFilters,
  });

  const allocatedStockLotIds = useMemo(
    () => getAllocatedStockLotIds(row),
    [row]
  );

  const updateVisibleStockLotIds = useCallback(() => {
    if (!apiRef.current || !stockLots?.data?.length) {
      setVisibleStockLotIds([]);
      return undefined;
    }

    try {
      const visibleRowIds = apiRef.current.getRowModels().keys();

      const pagination = apiRef.current.state.pagination;
      const start = pagination.page * pagination.pageSize;
      const end = start + pagination.pageSize;

      const visibleIds = Array.from(visibleRowIds)
        .slice(start, end)
        .filter((id): id is string => typeof id === 'string');

      setVisibleStockLotIds(visibleIds);
    } catch (error) {
      setVisibleStockLotIds([]);
    }
    return undefined;
  }, [stockLots?.data]);

  useEffect(() => {
    if (stockLots?.data?.length && apiRef.current) {
      const timeoutId = setTimeout(updateVisibleStockLotIds, 100);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [stockLots?.data, updateVisibleStockLotIds]);

  const { data: allocationsData, isLoading: isLoadingAllocations } =
    useServerSideQuery<AllocationQuerySchema, AllocationForReasoning>(
      useGetAllocationsServerSideQuery,
      visibleStockLotIds.length > 0
        ? {
            pageSize: DISABLE_PAGE_LIMIT,
            schema: [
              'id',
              'quant',
              'stockLot',
              'kitRequestLine',
              'productionRun',
              'kitRequest',
            ],
            filters: [
              {
                field: 'stockLot.id',
                operator: QueryFilterOperators.isAnyOf,
                value: visibleStockLotIds,
              },
            ],
          }
        : skipToken
    );

  const stockLotAllocations = useMemo(() => {
    const allocationsByStockLot = new Map<string, AllocationForReasoning[]>();

    allocationsData?.data?.forEach((allocation) => {
      if (allocation.stockLot) {
        const stockLotId =
          typeof allocation.stockLot === 'string'
            ? allocation.stockLot
            : (allocation.stockLot as { id: string }).id;

        if (!allocationsByStockLot.has(stockLotId)) {
          allocationsByStockLot.set(stockLotId, []);
        }
        allocationsByStockLot.get(stockLotId)!.push(allocation);
      }
    });

    return allocationsByStockLot;
  }, [allocationsData?.data]);

  const decoratedStockLots: DecoratedStockLot[] = useMemo(
    () =>
      stockLots?.data?.map((stockLot) => {
        const isAllocated = allocatedStockLotIds.has(stockLot.id);
        const allocations = stockLotAllocations?.get(stockLot.id) || [];

        const allocatedQuantity = row.allocations
          .filter((allocation) => allocation.stockLot?.id === stockLot.id)
          .reduce((sum, allocation) => sum + (allocation.quant || 0), 0);

        const allocationReason = kitRequest
          ? computeAllocationReasons(stockLot, kitRequest, {
              sourceLocationIds: facilityIds,
              kitRequestLine: row,
              isAllocated,
              allocations,
            })
          : null;

        return {
          ...stockLot,
          hasAllocation: isAllocated,
          allocatedQuantity,
          allocationReason,
        };
      }) || [],
    [
      stockLots?.data,
      allocatedStockLotIds,
      stockLotAllocations,
      row,
      kitRequest,
      facilityIds,
    ]
  );

  const totalQuantity = useMemo(
    () =>
      decoratedStockLots.reduce(
        (sum, stockLot) => sum + (stockLot.quant || 0),
        0
      ),
    [decoratedStockLots]
  );

  const stockLotColumns = useMemo(
    () => getStockLotColumns(totalQuantity, handleReplaceWithCustomTeamName),
    [totalQuantity, handleReplaceWithCustomTeamName]
  );

  const handleStateChange = useCallback(() => {
    updateVisibleStockLotIds();
  }, [updateVisibleStockLotIds]);

  return (
    <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
      <ExtendedDatagrid<DecoratedStockLot>
        gridName={DatagridNames.allocatedPartsStockLots}
        columns={stockLotColumns}
        rows={decoratedStockLots}
        loading={isLoadingStockLots || isLoadingAllocations}
        hideToolbar
        initialSx={{ minHeight: 300 }}
        noRows="You don't have any stock that matches this Part."
        pageSizeOptions={[5, 10, 25, 50]}
        apiRefServerSide={apiRef}
        onPageChange={handleStateChange}
        onPageSizeChange={handleStateChange}
        onSortModelChange={handleStateChange}
        onFilterModelChange={handleStateChange}
        initialVisibleColumns={[
          'lotId',
          'hasAllocation',
          'allocatedQuantity',
          'allocationReason',
          'quant',
        ]}
        initialSorting={{
          sortModel: [{ field: 'hasAllocation', sort: 'desc' }],
        }}
      />
    </Box>
  );
};
